import React from 'react';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import Button from 'components-stateless/DesignSystem/Atoms/Button';
import AlertGeneralSvg from 'components-stateless/DesignSystem/Atoms/Icons/AlertGeneral.svg?asComponent';
import AlertIssueSvg from 'components-stateless/DesignSystem/Atoms/Icons/AlertIssue.svg?asComponent';
import AlertWarningSvg from 'components-stateless/DesignSystem/Atoms/Icons/AlertWarning.svg?asComponent';
import CloseSvg from 'components-stateless/DesignSystem/Atoms/Icons/Close.svg?asComponent';
import TickCircleSvg from 'components-stateless/DesignSystem/Atoms/Icons/TickCircle.svg?asComponent';
import { COLOURS_AA } from '../Colours';

const DefaultSize = 'Small';
const DefaultType = 'Success';

export const SnackbarSizes = ['Small', 'Medium'] as const;
export const SnackbarTypes = ['Error', 'Warning', 'Info', 'Success'] as const;

export type SnackbarSizesType = typeof SnackbarSizes[number];

export type SnackbarTypesType = typeof SnackbarTypes[number];

export type SnackbarProps = {
  /**
   * id assigned to input. When it is not defined, a unique id will be assigned.
   */
  id?: string;
  /**
   * The callback invoked when snackbar closed
   */
  onClose?: () => void;
  /**
   * Default is `true`
   */
  isCloseable?: boolean;
  /**
   * Default size is `Small`
   */
  size?: SnackbarSizesType;
  /**
   * Default type is `Success`
   */
  type?: SnackbarTypesType;
  /**
   * For custom styling
   */
  style?: React.CSSProperties;
  /**
   * Action button copy text
   */
  actionButtonText?: string;
  /**
   * The callback invoked when action button is clicked
   */
  onClickActionButton?: () => void;
} & (
  | {
      /**
       * Alert message, in text.
       */
      message: string;
      /**
       * Alert message, in html.
       */
      htmlMessage?: never;
    }
  | {
      /**
       * Alert message, in html.
       */
      htmlMessage: string;
      /**
       * Alert message, in text.
       */
      message?: never;
    }
);

const Sizes = {
  Medium: {
    fontSize: 16,
    iconSize: 24,
    iconMarginX: 8,
    itemGap: 4,
    lineHeight: 28,
    paddingX: 8,
    paddingY: 8,
  },
  Small: {
    fontSize: 12,
    iconSize: 18,
    iconMarginX: 6,
    itemGap: 4,
    lineHeight: 20,
    paddingX: 4,
    paddingY: 8,
  },
};

const TypeStyle = {
  Error: {
    bg: COLOURS_AA.delete.BtnDeleteDefault,
    icon: AlertIssueSvg,
  },
  Warning: {
    bg: COLOURS_AA.alerts.AlertWarning,
    icon: AlertWarningSvg,
  },
  Info: {
    bg: COLOURS_AA.alerts.AlertInfo,
    icon: AlertGeneralSvg,
  },
  Success: {
    bg: COLOURS_AA.primary.BtnPrimaryDefault,
    icon: TickCircleSvg,
  },
};

const AlertBox = styled.div<{
  type: SnackbarTypesType;
  size: SnackbarSizesType;
}>`
  align-items: center;
  background-color: ${({ type }) => TypeStyle[type].bg};
  border-radius: 4px;
  color: ${COLOURS_AA.text.WhiteTxt};
  display: flex;
  font-size: ${({ size }) => Sizes[size].fontSize}px;
  font-weight: 600;
  gap: ${({ size }) => Sizes[size].itemGap}px;
  letter-spacing: -0.01em;
  line-height: ${({ size }) => Sizes[size].lineHeight}px;
  max-width: 60vw;
  padding-bottom: ${({ size }) => Sizes[size].paddingY}px;
  padding-left: ${({ size }) => Sizes[size].paddingX}px;
  padding-right: ${({ size }) => Sizes[size].paddingX}px;
  padding-top: ${({ size }) => Sizes[size].paddingY}px;
  width: max-content;

  svg {
    height: ${({ size }) => Sizes[size].iconSize}px;
    margin: 0 ${({ size }) => Sizes[size].iconMarginX}px;
    width: ${({ size }) => Sizes[size].iconSize}px;
    flex-shrink: 0;

    path {
      fill: currentColor;
    }
  }
`;

const CloseButton = styled.button`
  line-height: 1;
  padding: 0;
  margin: 0;
  cursor: pointer;
`;

const Filler = styled.div`
  width: 20px;
`;

const Snackbar = ({
  id,
  message,
  onClose,
  style,
  size = DefaultSize,
  type = DefaultType,
  isCloseable = true,
  actionButtonText,
  onClickActionButton,
  htmlMessage,
}: SnackbarProps) => {
  const TypeIcon = TypeStyle[type].icon;

  return (
    <AlertBox id={id} type={type} size={size} style={style} role="alert">
      <TypeIcon />
      {htmlMessage ? (
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlMessage) }}
        />
      ) : (
        message
      )}

      {actionButtonText && onClickActionButton ? (
        <Button
          buttonSize={size === 'Medium' ? 'Small' : 'X-Small'}
          buttonType="SpecialLight"
          shape="Square"
          onClick={() => {
            onClickActionButton();
            onClose?.();
          }}
          style={{ marginLeft: 4 }}
        >
          {actionButtonText}
        </Button>
      ) : null}

      {isCloseable ? (
        <CloseButton onClick={onClose} aria-label="Close">
          <CloseSvg size={size} />
        </CloseButton>
      ) : (
        <Filler />
      )}
    </AlertBox>
  );
};

export default Snackbar;
